import { initialState } from './state'
import {
  getTasksRoutine,
  refreshTaskCountsRoutine,
  taskQueryKey,
  getTaskRoutine,
  createTaskRoutine,
  updateTaskRoutine,
  deleteTaskRoutine,
  assignTaskRoutine,
  submitTaskRoutine,
  approveTaskRoutine,
  rejectTaskRoutine,
  starTaskRoutine,
  syncBlockersRoutine,
  updateApproversRoutine,
  assignExternalUserRoutine,
  removeExternalAssigneeRoutine,
  getProjectsRoutine,
  createProjectRoutine,
  updateProjectRoutine,
  deleteProjectRoutine,
  getFormTemplatesRoutine,
  getFormTemplateRoutine,
  createFormTemplateRoutine,
  updateFormTemplateRoutine,
  deleteFormTemplateRoutine,
  publishFormTemplateRoutine,
  cloneFormTemplateRoutine,
  saveFormResponseRoutine,
  saveApproverFormResponseRoutine,
  getFormVersionsRoutine,
  getConflictsRoutine,
  getTeamsRoutine,
  createTeamRoutine,
  updateTeamRoutine,
  deleteTeamRoutine,
  setTaskFiltersRoutine,
  clearTaskFiltersRoutine,
  bulkDeleteTasksRoutine,
  bulkUpdateTasksRoutine,
  bulkAssignTasksRoutine,
  selectTaskRoutine,
  selectAllTasksRoutine,
  clearSelectionRoutine,
  getTaskStatsRoutine,
  replicateTaskRoutine,
  holdTaskRoutine,
  resumeTaskRoutine,
  signOffTaskRoutine,
  bulkSignOffRoutine,
  getTemplateAssignmentsRoutine,
  createTemplateAssignmentRoutine,
  deleteTemplateAssignmentRoutine,
  getTaskSettingsRoutine,
  updateTaskSettingsRoutine,
  getProjectDashboardRoutine,
  saveAndSubmitTaskRoutine
} from './actions'

// axios-case-converter converts ALL response object keys to camelCase, including
// form response keys which are form field identifiers (e.g. has_increased_fire_risk
// becomes hasIncreasedFireRisk). But the schema field.key values remain as snake_case
// strings. This normalizer converts response keys back to snake_case so they match.
const toSnakeCase = str => str.replace(/[A-Z]/g, l => `_${l.toLowerCase()}`)

const snakeCaseKeys = obj => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj
  const result = {}
  for (const [key, value] of Object.entries(obj)) {
    const sk = toSnakeCase(key)
    if (Array.isArray(value)) {
      result[sk] = value.map(entry =>
        entry && typeof entry === 'object' && !Array.isArray(entry)
          ? snakeCaseKeys(entry)
          : entry
      )
    } else if (value && typeof value === 'object') {
      result[sk] = snakeCaseKeys(value)
    } else {
      result[sk] = value
    }
  }
  return result
}

const normalizeTask = task => {
  if (!task?.formResponse?.responses) return task
  return {
    ...task,
    formResponse: {
      ...task.formResponse,
      responses: snakeCaseKeys(task.formResponse.responses)
    }
  }
}

const CLOSED_STATUSES = ['completed', 'on_hold']

export const leavesStatusFilter = (task, filters = {}) => {
  const status = task?.status
  if (!status) return false
  if (filters.status && filters.status !== 'all') {
    if (filters.status === 'overdue') {
      if (status === 'completed') return true
    } else if (status !== filters.status) {
      return true
    }
  }
  return !!filters.open && CLOSED_STATUSES.includes(status)
}

const isStaleUpdate = (incoming, existing) => {
  const next = Date.parse(incoming?.updatedAt)
  const prev = Date.parse(existing?.updatedAt)
  return !Number.isNaN(next) && !Number.isNaN(prev) && next < prev
}

const mergeCurrentTask = (state, normalized) => (
  state.currentTask?.id === normalized.id && !isStaleUpdate(normalized, state.currentTask)
    ? { ...state.currentTask, ...normalized }
    : state.currentTask
)

const mergeTaskIntoList = (state, normalized) => {
  const listed = state.tasks.find(task => task.id === normalized.id)
  if (!listed || isStaleUpdate(normalized, listed)) return state
  const merged = { ...listed, ...normalized }
  if (leavesStatusFilter(merged, state.filters)) {
    return {
      ...state,
      tasks: state.tasks.filter(task => task.id !== normalized.id),
      tasksTotal: Math.max(0, (state.tasksTotal || 0) - 1),
      selectedTaskIds: state.selectedTaskIds.filter(id => id !== normalized.id)
    }
  }
  return { ...state, tasks: state.tasks.map(task => (task.id === normalized.id ? merged : task)) }
}

export default (state = initialState, action) => {
  switch (action.type) {
    // Get Tasks
    case getTasksRoutine.TRIGGER:
      return { ...state, tasksLoading: true }
    case getTasksRoutine.SUCCESS: {
      if (action.payload.queryKey && action.payload.queryKey !== taskQueryKey(state.filters)) return state
      const incomingPage = action.payload.currentPage || 1
      const incomingRows = (action.payload.data || []).map(normalizeTask)
      // Page > 1 means the user is loading more — append to the existing list
      // with id-dedup so concurrent page-1 refetches don't duplicate rows.
      // Page 1 (or no meta) means a fresh fetch — replace.
      const dedupeById = (list) => {
        const seen = new Set()
        return list.filter(x => x?.id && !seen.has(x.id) && seen.add(x.id))
      }
      return {
        ...state,
        tasks: (action.payload.replace || incomingPage === 1)
          ? incomingRows
          : dedupeById([...state.tasks, ...incomingRows]),
        tasksTotalPages: action.payload.totalPages || 1,
        tasksCurrentPage: incomingPage,
        tasksTotal: action.payload.total ?? state.tasksTotal,
        windowCounts: action.payload.windows ?? state.windowCounts,
        viewCounts: incomingPage === 1 && !state.filters.category ? (action.payload.viewCounts ?? state.viewCounts) : state.viewCounts,
        tasksLoading: false
      }
    }
    case refreshTaskCountsRoutine.SUCCESS: {
      if (action.payload?.queryKey !== taskQueryKey(state.filters)) return state
      return {
        ...state,
        windowCounts: action.payload.windows ?? state.windowCounts,
        viewCounts: action.payload.viewCounts ?? state.viewCounts,
        tasksTotal: action.payload.total ?? state.tasksTotal
      }
    }
    case getTasksRoutine.FAILURE:
      return { ...state, tasksLoading: false }

    // Task Stats
    case getTaskStatsRoutine.TRIGGER:
      return { ...state, taskStatsLoading: true }
    case getTaskStatsRoutine.SUCCESS:
      return { ...state, taskStats: action.payload, taskStatsLoading: false }
    case getTaskStatsRoutine.FAILURE:
      return { ...state, taskStatsLoading: false }

    // Get Single Task
    case getTaskRoutine.TRIGGER:
      return { ...state, currentTaskLoading: true, currentTaskNotFound: false }
    case getTaskRoutine.SUCCESS: {
      const normalized = normalizeTask(action.payload)
      return {
        ...state,
        tasks: state.tasks.map(task =>
          task.id === normalized.id ? { ...task, ...normalized } : task
        ),
        currentTask: state.currentTask?.id === normalized.id
          ? { ...state.currentTask, ...normalized }
          : normalized,
        currentTaskLoading: false,
        currentTaskNotFound: false
      }
    }
    case getTaskRoutine.FAILURE:
      return {
        ...state,
        currentTaskLoading: false,
        currentTaskNotFound: !!action.payload?.notFound
      }

    // Create Task
    case createTaskRoutine.TRIGGER:
      return { ...state, createTaskLoading: true }
    case createTaskRoutine.FAILURE:
      return { ...state, createTaskLoading: false }
    case createTaskRoutine.SUCCESS:
    case replicateTaskRoutine.SUCCESS: {
      const newTasks = Array.isArray(action.payload)
        ? action.payload.map(normalizeTask)
        : [normalizeTask(action.payload)]
      const existingIds = new Set(state.tasks.map(task => task.id))
      const added = newTasks.filter(task => !existingIds.has(task.id))
      return {
        ...state,
        tasks: [...added, ...state.tasks.map(task => newTasks.find(n => n.id === task.id) || task)],
        tasksTotal: (state.tasksTotal || 0) + added.length,
        createTaskLoading: false
      }
    }

    // Update Task
    case updateTaskRoutine.SUCCESS: {
      const normalized = normalizeTask(action.payload)
      return {
        ...mergeTaskIntoList(state, normalized),
        currentTask: mergeCurrentTask(state, normalized)
      }
    }

    // Delete Task
    case deleteTaskRoutine.SUCCESS: {
      const wasListed = state.tasks.some(task => task.id === action.payload)
      return {
        ...state,
        tasks: state.tasks.filter(task => task.id !== action.payload),
        tasksTotal: wasListed ? Math.max(0, (state.tasksTotal || 0) - 1) : state.tasksTotal,
        selectedTaskIds: state.selectedTaskIds.filter(id => id !== action.payload)
      }
    }

    // Projects
    case getProjectsRoutine.TRIGGER:
      return { ...state, projectsLoading: true }
    case getProjectsRoutine.SUCCESS:
      return { ...state, projects: action.payload, projectsLoading: false }
    case getProjectsRoutine.FAILURE:
      return { ...state, projectsLoading: false }

    case createProjectRoutine.SUCCESS:
      return { ...state, projects: [action.payload, ...state.projects] }

    case updateProjectRoutine.SUCCESS:
      return {
        ...state,
        projects: state.projects.map(project =>
          project.id === action.payload.id ? action.payload : project
        )
      }

    case deleteProjectRoutine.SUCCESS:
      return {
        ...state,
        projects: state.projects.filter(project => project.id !== action.payload)
      }

    // Form Templates
    case getFormTemplatesRoutine.TRIGGER:
      return { ...state, formTemplatesLoading: true }
    case getFormTemplatesRoutine.SUCCESS:
      return { ...state, formTemplates: action.payload, formTemplatesLoading: false }
    case getFormTemplatesRoutine.FAILURE:
      return { ...state, formTemplatesLoading: false }

    case getFormTemplateRoutine.TRIGGER:
      return { ...state, currentFormTemplateLoading: true }
    case getFormTemplateRoutine.SUCCESS:
      return { ...state, currentFormTemplate: action.payload, currentFormTemplateLoading: false }
    case getFormTemplateRoutine.FAILURE:
      return { ...state, currentFormTemplateLoading: false }

    case createFormTemplateRoutine.TRIGGER:
      return { ...state, formTemplateSaving: true }
    case createFormTemplateRoutine.SUCCESS:
      return {
        ...state,
        formTemplates: [action.payload, ...state.formTemplates],
        formTemplateSaving: false
      }
    case createFormTemplateRoutine.FAILURE:
      return { ...state, formTemplateSaving: false }

    case updateFormTemplateRoutine.TRIGGER:
      return { ...state, formTemplateSaving: true }
    case updateFormTemplateRoutine.SUCCESS:
      return {
        ...state,
        formTemplates: state.formTemplates.map(t =>
          t.id === action.payload.id ? action.payload : t
        ),
        currentFormTemplate: state.currentFormTemplate?.id === action.payload.id
          ? action.payload
          : state.currentFormTemplate,
        formTemplateSaving: false
      }
    case updateFormTemplateRoutine.FAILURE:
      return { ...state, formTemplateSaving: false }

    case deleteFormTemplateRoutine.SUCCESS:
      return {
        ...state,
        formTemplates: state.formTemplates.filter(t => t.id !== action.payload)
      }

    case publishFormTemplateRoutine.SUCCESS:
      return {
        ...state,
        formTemplates: state.formTemplates.map(t =>
          t.id === action.payload.id ? action.payload : t
        ),
        currentFormTemplate: state.currentFormTemplate?.id === action.payload.id
          ? action.payload
          : state.currentFormTemplate
      }

    case cloneFormTemplateRoutine.SUCCESS:
      return {
        ...state,
        formTemplates: [action.payload, ...state.formTemplates]
      }

    // Teams
    case getTeamsRoutine.TRIGGER:
      return { ...state, teamsLoading: true }
    case getTeamsRoutine.SUCCESS:
      return { ...state, teams: action.payload, teamsLoading: false }
    case getTeamsRoutine.FAILURE:
      return { ...state, teamsLoading: false }

    case createTeamRoutine.SUCCESS:
      return { ...state, teams: [action.payload, ...state.teams] }

    case updateTeamRoutine.SUCCESS:
      return {
        ...state,
        teams: state.teams.map(team =>
          team.id === action.payload.id ? action.payload : team
        )
      }

    case deleteTeamRoutine.SUCCESS:
      return {
        ...state,
        teams: state.teams.filter(team => team.id !== action.payload)
      }

    // Form Responses
    case saveFormResponseRoutine.TRIGGER:
      return { ...state, formResponseSaving: true }
    case saveFormResponseRoutine.SUCCESS:
      return { ...state, formResponseSaving: false }
    case saveFormResponseRoutine.FAILURE:
      return { ...state, formResponseSaving: false }

    // Save and Submit (combined operation)
    case saveAndSubmitTaskRoutine.TRIGGER:
    case saveAndSubmitTaskRoutine.REQUEST:
      return { ...state, formResponseSaving: true }
    case saveAndSubmitTaskRoutine.SUCCESS:
      return { ...state, formResponseSaving: false }
    case saveAndSubmitTaskRoutine.FAILURE:
      return { ...state, formResponseSaving: false }

    // Approver Form Response
    case saveApproverFormResponseRoutine.TRIGGER:
      return { ...state, approverFormSaving: true }
    case saveApproverFormResponseRoutine.SUCCESS:
      return { ...state, approverFormSaving: false }
    case saveApproverFormResponseRoutine.FAILURE:
      return { ...state, approverFormSaving: false }

    // Form Versions
    case getFormVersionsRoutine.TRIGGER:
      return { ...state, formVersionsLoading: true }
    case getFormVersionsRoutine.SUCCESS:
      return { ...state, formVersions: action.payload, formVersionsLoading: false }
    case getFormVersionsRoutine.FAILURE:
      return { ...state, formVersionsLoading: false }

    // Conflicts
    case getConflictsRoutine.TRIGGER:
      return { ...state, conflictsLoading: true }
    case getConflictsRoutine.SUCCESS:
      return { ...state, conflicts: action.payload, conflictsLoading: false }
    case getConflictsRoutine.FAILURE:
      return { ...state, conflictsLoading: false }

    // Task Actions (submit, approve, reject, assign, etc.)
    case submitTaskRoutine.REQUEST:
    case approveTaskRoutine.REQUEST:
    case rejectTaskRoutine.REQUEST:
    case assignTaskRoutine.REQUEST:
    case updateApproversRoutine.REQUEST:
    case syncBlockersRoutine.REQUEST:
    case assignExternalUserRoutine.REQUEST:
    case removeExternalAssigneeRoutine.REQUEST:
    case holdTaskRoutine.REQUEST:
    case resumeTaskRoutine.REQUEST:
    case signOffTaskRoutine.REQUEST:
      return { ...state, taskActionLoading: true }
    case submitTaskRoutine.SUCCESS:
    case approveTaskRoutine.SUCCESS:
    case rejectTaskRoutine.SUCCESS:
    case assignTaskRoutine.SUCCESS:
    case updateApproversRoutine.SUCCESS:
    case syncBlockersRoutine.SUCCESS:
    case assignExternalUserRoutine.SUCCESS:
    case removeExternalAssigneeRoutine.SUCCESS:
    case holdTaskRoutine.SUCCESS:
    case resumeTaskRoutine.SUCCESS:
    case signOffTaskRoutine.SUCCESS: {
      const normalized = action.payload ? normalizeTask(action.payload) : null
      if (!normalized?.id) return { ...state, taskActionLoading: false }
      return {
        ...mergeTaskIntoList(state, normalized),
        taskActionLoading: false,
        currentTask: mergeCurrentTask(state, normalized)
      }
    }
    case submitTaskRoutine.FAILURE:
    case approveTaskRoutine.FAILURE:
    case rejectTaskRoutine.FAILURE:
    case assignTaskRoutine.FAILURE:
    case updateApproversRoutine.FAILURE:
    case syncBlockersRoutine.FAILURE:
    case assignExternalUserRoutine.FAILURE:
    case removeExternalAssigneeRoutine.FAILURE:
    case holdTaskRoutine.FAILURE:
    case resumeTaskRoutine.FAILURE:
    case signOffTaskRoutine.FAILURE:
      return { ...state, taskActionLoading: false }

    // Star (no loading state needed, instant toggle)
    case starTaskRoutine.FAILURE:
      return state

    // Selection
    case selectTaskRoutine.SUCCESS: {
      const taskId = action.payload
      const isSelected = state.selectedTaskIds.includes(taskId)
      return {
        ...state,
        selectedTaskIds: isSelected
          ? state.selectedTaskIds.filter(id => id !== taskId)
          : [...state.selectedTaskIds, taskId]
      }
    }
    case selectAllTasksRoutine.SUCCESS:
      return { ...state, selectedTaskIds: action.payload || [] }
    case clearSelectionRoutine.SUCCESS:
      return { ...state, selectedTaskIds: [] }

    // Bulk Operations
    case bulkDeleteTasksRoutine.REQUEST:
    case bulkUpdateTasksRoutine.REQUEST:
    case bulkAssignTasksRoutine.REQUEST:
    case bulkSignOffRoutine.REQUEST:
      return { ...state, bulkActionLoading: true }
    case bulkDeleteTasksRoutine.SUCCESS:
    case bulkUpdateTasksRoutine.SUCCESS:
    case bulkAssignTasksRoutine.SUCCESS:
    case bulkSignOffRoutine.SUCCESS:
    case bulkDeleteTasksRoutine.FAILURE:
    case bulkUpdateTasksRoutine.FAILURE:
    case bulkAssignTasksRoutine.FAILURE:
    case bulkSignOffRoutine.FAILURE:
      return { ...state, bulkActionLoading: false }

    // Template Assignments
    case getTemplateAssignmentsRoutine.TRIGGER:
      return { ...state, templateAssignmentsLoading: true }
    case getTemplateAssignmentsRoutine.SUCCESS:
      return { ...state, templateAssignments: action.payload, templateAssignmentsLoading: false }
    case getTemplateAssignmentsRoutine.FAILURE:
      return { ...state, templateAssignmentsLoading: false }

    case createTemplateAssignmentRoutine.SUCCESS:
      return { ...state, templateAssignments: [action.payload, ...state.templateAssignments] }

    case deleteTemplateAssignmentRoutine.SUCCESS:
      return {
        ...state,
        templateAssignments: state.templateAssignments.filter(a => a.id !== action.payload)
      }

    // Task Settings
    case getTaskSettingsRoutine.TRIGGER:
      return { ...state, taskSettingsLoading: true }
    case getTaskSettingsRoutine.SUCCESS:
      return { ...state, taskSettings: action.payload, taskSettingsLoading: false }
    case getTaskSettingsRoutine.FAILURE:
      return { ...state, taskSettingsLoading: false }

    case updateTaskSettingsRoutine.TRIGGER:
      return { ...state, taskSettingsLoading: true }
    case updateTaskSettingsRoutine.SUCCESS:
      return { ...state, taskSettings: action.payload, taskSettingsLoading: false }
    case updateTaskSettingsRoutine.FAILURE:
      return { ...state, taskSettingsLoading: false }

    // Project Dashboard
    case getProjectDashboardRoutine.TRIGGER:
      return { ...state, projectDashboardLoading: true }
    case getProjectDashboardRoutine.SUCCESS:
      return { ...state, projectDashboard: action.payload, projectDashboardLoading: false }
    case getProjectDashboardRoutine.FAILURE:
      return { ...state, projectDashboardLoading: false }

    // Filters
    case setTaskFiltersRoutine.SUCCESS:
      return { ...state, filters: { ...state.filters, ...action.payload } }
    case clearTaskFiltersRoutine.SUCCESS:
      return { ...state, filters: { ...initialState.filters, window: state.filters.window }, selectedTaskIds: [] }

    default:
      return state
  }
}
